import { useWindowSize } from "hooks/useWindowSize";
import { ConditionalLink } from "components/conditionalLink";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { Label } from "components/DesignSystem/TextInput/TextInput";
import { PriceInput } from "components/PriceInput/PriceInput";
import dayjs from "dayjs";
import { Form, Formik } from "formik";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useGetEntityBanksQuery } from "store/apis/bankConnections";
import {
  useUploadReconcileCSVMutation,
  useUploadAiReconcileStatementMutation,
} from "store/apis/reconciliation";
import { mixed, object, string } from "yup";
import { CsvColumnsMap } from "./CsvColumnsMap";
import { FileInput } from "components/FileInput/FileInput";
import { DateField } from "components/DesignSystem/DateInput/DateField";
import { CalendarIcon } from "components/icons/CalendarIcon";
import { RightArrow } from "components/icons/RightArrow";
import { useHistory } from "react-router-dom";
import { ReconciliationHeader } from "./ReconciliationTxn";
import { MM_DD_YYYY, YYYY_MM_DD } from "constants/date";
import { useQuery } from "hooks/useQuery";
import { CSV, PDF } from "constants/fileTypes";
import {
  ACCOUNT_STATEMENT_ACCEPTED_FILES,
  UNPARSED_CSV_STATEMENT_ERROR,
} from "./constants";
import { isPdfFile } from "util/isPdfFile";
import { isStripeBank } from "utils/bankNameAcc";
import { StripeReconcile } from "./StripeReconcile";

export const Reconcile = ({
  isOpen,
  onClose,
  bankAccountId,
  mode = "manual",
}: {
  isOpen: boolean;
  onClose: () => void;
  bankAccountId: string;
  mode?: "manual" | "ai";
}) => {
  const isAiMode = mode === "ai";
  const { isMobile } = useWindowSize();
  const { alertToast } = useToast();
  const {
    isOpen: isCsvColumnsMapModalOpen,
    close: csvColumnsMapModalClose,
    open,
  } = useModal();
  const [uploadReconcileCSV, { data: uploadedCsvData }] =
    useUploadReconcileCSVMutation();
  const [uploadAiReconcileStatement] = useUploadAiReconcileStatementMutation();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const history = useHistory();
  const query = useQuery();

  const { data: ledger } = useGetEntityBanksQuery(
    { entityId, groupId },
    { skip: !entityId || !groupId }
  );

  const { accounts = [] } = ledger || {};

  const reconcileBank = accounts.find(
    ({ account: { uuid } }) => uuid === bankAccountId
  );

  const reconcileAccount = reconcileBank?.account;

  const onSubmit = async (values: {
    reconcilation_ending_date: string;
    reconcilation_starting_date: string;
    customer_statement_ending_balance: string;
    csv?: File;
  }) => {
    try {
      const payload = {
        entity_bank_account_id: reconcileAccount?.uuid!,
        csv: values.csv,
        reconcilation_ending_date: dayjs(
          values.reconcilation_ending_date
        ).format(MM_DD_YYYY),
        reconcilation_starting_date: dayjs(
          values.reconcilation_starting_date
        ).format(MM_DD_YYYY),
        customer_statement_ending_balance:
          values.customer_statement_ending_balance,
      };

      if (isAiMode) {
        const data = await uploadAiReconcileStatement({
          groupId,
          entityId,
          payload: { ...payload, csv: values.csv! },
        }).unwrap();

        history.push(
          `/books/ai-reconciliation/${
            data.reconciliation_state_id
          }?${query.toString()}`
        );
        return;
      }

      const data = await uploadReconcileCSV({
        groupId,
        entityId,
        payload,
      }).unwrap();

      const reconciliationDateParams = new URLSearchParams(query);
      reconciliationDateParams.set(
        "START_DATE",
        dayjs(reconcileAccount?.reconcilation_start_date).format(YYYY_MM_DD)
      );
      reconciliationDateParams.set(
        "END_DATE",
        dayjs(values.reconcilation_ending_date).format(YYYY_MM_DD)
      );

      const isPdfStatement = isPdfFile(values.csv?.name);
      const hasColumnsToMap =
        Boolean(values.csv) &&
        !isPdfStatement &&
        Boolean(data.labels_in_csv?.length);
      const isUnparsedCsv =
        Boolean(values.csv) && !isPdfStatement && !data.labels_in_csv?.length;

      if (isUnparsedCsv) {
        alertToast({ message: UNPARSED_CSV_STATEMENT_ERROR });
        return;
      }

      if (hasColumnsToMap) {
        history.replace({ search: reconciliationDateParams.toString() });
        open();
      } else {
        history.push(
          `/books/reconciliation/${
            data.reconcilation_state_id
          }?${reconciliationDateParams.toString()}`
        );
      }
    } catch (error: any) {
      alertToast({ message: error?.data?.error?.message });
    }
  };

  const reconciliationStartDate = reconcileAccount?.last_reconciled_date
    ? dayjs(reconcileAccount.last_reconciled_date).add(1, "day")
    : dayjs(reconcileAccount?.reconcilation_start_date);

  if (!reconcileBank) {
    return null;
  }

  if (isStripeBank(reconcileBank)) {
    return (
      <StripeReconcile
        isOpen={isOpen}
        onClose={onClose}
        bankAccountId={bankAccountId}
        brandName={reconcileBank?.bank_brand.name ?? ""}
        accountName={reconcileAccount?.nickname ?? ""}
        periodStart={reconciliationStartDate}
      />
    );
  }

  return (
    <>
      <Modal.Root open={isOpen} onOpenChange={onClose}>
        <Formik
          initialValues={{
            reconcilation_ending_date: "",
            reconcilation_starting_date:
              reconciliationStartDate.format(YYYY_MM_DD),
            customer_statement_ending_balance: "",
            // @ts-ignore
            csv: "",
          }}
          onSubmit={onSubmit}
          validationSchema={object({
            reconcilation_ending_date: string().required("Please select date"),
            reconcilation_starting_date:
              string().required("Please select date"),
            customer_statement_ending_balance: string().required(
              "Add statement ending balance"
            ),
            csv: isAiMode
              ? mixed().test(
                  "required-file",
                  "Please upload the account statement",
                  (value) => Boolean(value)
                )
              : mixed(),
          })}
          validateOnMount
          validateOnChange
        >
          {({
            values: { csv, reconcilation_starting_date },
            setFieldValue,
            isValid,
            isSubmitting,
            submitForm,
          }) => {
            const accountStatementFileType = isPdfFile(csv?.name) ? PDF : CSV;

            return (
              <Form className="t-m-0 t-w-full">
                <Modal.Content>
                  <Modal.Header>
                    <ReconciliationHeader
                      brandName={reconcileBank?.bank_brand.name ?? ""}
                      accountName={reconcileAccount?.nickname ?? ""}
                    />
                  </Modal.Header>
                  <Modal.Body className="t-flex t-gap-4 t-flex-col t-w-full">
                    <div>
                      <Label required>Reconciliation period</Label>
                      <div className="t-flex t-justify-between t-flex-row t-gap-2 t-items-center">
                        <div className="t-flex-1 sm:t-min-w-0">
                          <DateField
                            name="reconcilation_starting_date"
                            dateProps={{
                              placeholder: "Select start date",
                              customSize: "small",
                              showErrorOnceTouched: true,
                              portalId: "billing-start-0",
                              required: true,
                              maxDate: new Date(),
                              disabled: true,
                            }}
                          />
                        </div>

                        <span className="t-text-text-30 sm:t-shrink-0">
                          <RightArrow />
                        </span>

                        <div className="t-flex-1 sm:t-min-w-0">
                          <DateField
                            name="reconcilation_ending_date"
                            dateProps={{
                              placeholder: "Select end date",
                              customSize: "small",
                              showErrorOnceTouched: true,
                              portalId: "billing-end-0",
                              maxDate: new Date(),
                              minDate: new Date(reconcilation_starting_date),
                              rightComponent: (
                                <span className="t-text-text-30">
                                  <CalendarIcon />
                                </span>
                              ),
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    <PriceInput
                      name="customer_statement_ending_balance"
                      label="Statement ending balance"
                      placeholder="Enter amount"
                      required
                    />
                    <div>
                      <FileInput
                        name="csv"
                        withForm
                        onDelete={() => setFieldValue("csv", null)}
                        label="Account statement"
                        required={isAiMode}
                        file={
                          csv && {
                            name: csv.name,
                            file_type: accountStatementFileType,
                            is_previewable: false,
                            uuid: "",
                          }
                        }
                        accept={ACCOUNT_STATEMENT_ACCEPTED_FILES}
                      />
                      {!isAiMode && (
                        <p className="t-m-0 t-text-body-sm-italic t-italic  t-mt-3">
                          <ConditionalLink
                            to="https://docs.google.com/spreadsheets/d/e/2PACX-1vRPFV01D9Tk2KWIjqXHeu1aSCMUq-bVOCY93LvfEXphj2A_jFWy7Xiy5MWg_7ZE9cI6lksThNGjZejA/pub?output=csv"
                            className="t-text-purple"
                            target={isMobile ? "_self" : undefined}
                          >
                            Click here
                          </ConditionalLink>{" "}
                          to download the sample format
                        </p>
                      )}
                    </div>
                  </Modal.Body>
                  <Modal.Footer className="t-flex t-items-center t-justify-end t-gap-3">
                    <Button
                      type="reset"
                      onClick={onClose}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      customType="primary"
                      type="submit"
                      disabled={!isValid || isSubmitting}
                      isLoading={isSubmitting}
                      onClick={submitForm}
                    >
                      {isAiMode ? "Run AI reconciliation" : "Next"}
                    </Button>
                  </Modal.Footer>
                </Modal.Content>
              </Form>
            );
          }}
        </Formik>
      </Modal.Root>
      <CsvColumnsMap
        isOpen={isCsvColumnsMapModalOpen}
        onClose={csvColumnsMapModalClose}
        uploadedCsvData={uploadedCsvData}
      />
    </>
  );
};
